import { isWeb, useGetUserProfileQuery } from "@stepapp/core";
import {
  Block,
  Button,
  CircleLoader,
  Divider,
  Image,
  palette,
  spacing,
  Typography,
} from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { Fragment, memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { error, success } from "../../../src/assets/images";
import { PromoBanner } from "../../../src/components";
import { useAppDispatch, useAppSelector } from "../../../src/hooks";
import { AppStorage } from "../../../src/services/AppStorage";
import { clearDraftService } from "../../../src/store/draftReservation";
import { setUserData } from "../../../src/store/user";

enum ReservationStatus {
  IDLE = "IDLE",
  LOADING = "LOADING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

const ServiceConfirmationScreen = () => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const { replace } = useRouter();
  const { orderId } = useLocalSearchParams();
  const dispatch = useAppDispatch();
  const [status, setStatus] = React.useState<ReservationStatus>(ReservationStatus.IDLE);
  useGetUserProfileQuery({
    variables: { isCustomerProfile: true },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      dispatch(setUserData(data?.getMyProfile));
    },
  });

  const { firstName, lastName, email, profile } = useAppSelector(({ user }) => user);

  const orderInfoData = [
    { label: t("common.id"), value: orderId },
    { label: t("common.name"), value: `${firstName} ${lastName}` },
    { label: t("common.phone"), value: profile.phoneNumber },
    { label: t("common.email"), value: email, id: "userEmail" },
  ];

  const handleReservationCreate = useCallback(() => {
    setStatus(ReservationStatus.LOADING);
    AppStorage().set("visitsAfterReservation", 1);
    appDispatch(clearDraftService());

    return setStatus(ReservationStatus.SUCCESS);
  }, [appDispatch]);

  useEffect(() => {
    handleReservationCreate();
  }, [handleReservationCreate]);

  const isSuccess = status === ReservationStatus.SUCCESS;

  const navigateAfterConfirm = useCallback(() => {
    replace(isSuccess ? "/calendar/" : "/");
  }, [isSuccess, replace]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      {[ReservationStatus.LOADING, ReservationStatus.IDLE].includes(status) ? (
        <Block>
          <Typography center variant="newH2">
            {t("screens.service.loader")}
          </Typography>
          <Block alignCenter paddingTop={20}>
            <CircleLoader />
          </Block>
        </Block>
      ) : (
        <>
          <Block>
            <Image
              height={spacing.unit * 9}
              source={isSuccess ? success : error}
              width={spacing.unit * 9}
              style={styles.center}
            />

            <Block paddingTop={3}>
              <Typography center variant="newH2">
                {isSuccess
                  ? t("screens.service.reservationSuccess")
                  : t("screens.service.reservationError")}
              </Typography>
            </Block>

            <Block paddingTop={2}>
              <Typography center variant="paragraph">
                {isSuccess
                  ? t("screens.service.reservationSuccessDescription")
                  : t("screens.service.reservationErrorDescription")}
              </Typography>
            </Block>

            {isWeb && <PromoBanner />}

            <Block style={styles.orderInfo}>
              {orderInfoData.map((item, index) => (
                <Fragment key={item.label}>
                  <Block row>
                    <Typography variant="paragraph" type="secondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="paragraph"
                      id={item.id}
                      numberOfLines={1}
                      style={styles.orderInfoValue}
                    >
                      {item.value}
                    </Typography>
                  </Block>
                  {index !== orderInfoData.length - 1 && <Divider />}
                </Fragment>
              ))}
            </Block>
          </Block>

          <Button
            label={isSuccess ? t("screens.service.showCalendar") : t("screens.service.backToHome")}
            onPress={() => navigateAfterConfirm()}
            type="primary"
            testID="navigateToCalendar"
          />
        </>
      )}
    </ScrollView>
  );
};

export default memo(ServiceConfirmationScreen);

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.unit * 2,
    paddingBottom: spacing.unit * 2,
    flex: 1,
    justifyContent: isWeb ? "flex-start" : "space-between",
  },
  orderInfo: {
    backgroundColor: palette.common.grayBackground,
    padding: spacing.unit * 2,
    borderRadius: spacing.radius,
    marginVertical: spacing.unit * 3,
  },
  center: {
    alignSelf: "center",
  },
  orderInfoValue: {
    textAlign: "right",
    flex: 1,
    marginLeft: spacing.unit * 2,
  },
});
